import React from 'react';
import { Combobox } from '@headlessui/react';
import LoadingSkeleton from 'components/LoadingSkeleton';
import classNames from 'styles/utils/classNames';

export const ADDRESS_INPUT_ID = 'address-search-input';

interface Props {
  handleSubmit: (address: google.maps.places.AutocompletePrediction | null) => void;
  handleAutcompleteSuggestions: (e: React.ChangeEvent<HTMLInputElement>) => void;
  addressString?: string;
  suggestedAddresses?: google.maps.places.AutocompletePrediction[];
  activeAutocompleteAddress?: google.maps.places.AutocompletePrediction | null;
  placeholder: string;
  className?: string;
  suggestionContainerClassName?: string;
  /** Replaces the default dropdown surface classes (position, radius, shadow). */
  dropdownClassName?: string;
  /** Replaces the default option row classes. */
  optionClassName?: string;
  id?: string;
  name?: string;
  autoComplete?: string;
  type?: string;
  required?: boolean;
  disabled?: boolean;
  onBlur?: React.FocusEventHandler<HTMLInputElement> | undefined;
  onFocus?: React.FocusEventHandler<HTMLInputElement> | undefined;
  // New props for enhanced functionality
  isLoadingAutocomplete?: boolean;
  autocompleteInputLength?: number;
  autocompleteMinLength?: number;
  isGoogleMapsAvailable?: boolean;
}

export default function AddressSearchEnhanced({
  handleSubmit,
  suggestedAddresses,
  addressString,
  activeAutocompleteAddress,
  handleAutcompleteSuggestions,
  placeholder,
  className,
  suggestionContainerClassName,
  dropdownClassName,
  optionClassName,
  id,
  name,
  autoComplete,
  type,
  required,
  onBlur,
  onFocus,
  disabled,
  isLoadingAutocomplete = false,
  autocompleteInputLength = 0,
  autocompleteMinLength = 3,
  isGoogleMapsAvailable = true,
}: Props) {
  const [isFocused, setIsFocused] = React.useState(false);

  // Simple logic: show dropdown when focused and has input
  const hasInput = addressString && addressString.trim().length > 0;

  // Determine what to show in the dropdown
  const isBelowMinLength =
    autocompleteInputLength > 0 && autocompleteInputLength < autocompleteMinLength;
  const isSearching = isLoadingAutocomplete && autocompleteInputLength >= autocompleteMinLength;
  const hasResults = suggestedAddresses && suggestedAddresses.length > 0;
  const showNoResults =
    !isLoadingAutocomplete && autocompleteInputLength >= autocompleteMinLength && !hasResults;

  // Only show dropdown if there's something to display
  const hasContent = isBelowMinLength || isSearching || hasResults || showNoResults;
  const showDropdown = isFocused && hasInput && hasContent && !disabled;

  const handleSelect = (address: google.maps.places.AutocompletePrediction | null) => {
    // Close dropdown
    setIsFocused(false);

    // Submit the selection
    handleSubmit(address);

    // Blur the input element after a short delay to ensure the selection is processed
    setTimeout(() => {
      const input = document.getElementById(id || ADDRESS_INPUT_ID) as HTMLInputElement;
      if (input) {
        input.blur();
      }
    }, 50);
  };

  return (
    <Combobox as="div" className="w-full" value={activeAutocompleteAddress} onChange={handleSelect}>
      <Combobox.Label className="hidden">Address</Combobox.Label>
      <div className="relative">
        <Combobox.Input
          id={id || ADDRESS_INPUT_ID}
          name={name}
          autoComplete={autoComplete}
          type={type}
          className={classNames(className, disabled && 'cursor-not-allowed select-none opacity-50')}
          placeholder={placeholder}
          required={required}
          onChange={(e) => !disabled && handleAutcompleteSuggestions(e)}
          onBlur={(e) => {
            // Delay hiding dropdown to allow click events to process
            setTimeout(() => setIsFocused(false), 200);
            onBlur?.(e);
          }}
          onFocus={(e) => {
            setIsFocused(true);
            onFocus?.(e);
          }}
          value={addressString}
          displayValue={(a: google.maps.places.AutocompletePrediction | null) => {
            return a?.description || addressString || '';
          }}
        />

        {/* Dropdown */}
        {showDropdown && (
          <div
            className={classNames(
              dropdownClassName ||
                'absolute top-8 z-20 mt-1 rounded-lg bg-white shadow-popover ring-1 ring-black ring-opacity-5',
              suggestionContainerClassName || 'w-full',
            )}
          >
            {/* Below minimum length message */}
            {isBelowMinLength && (
              <div className="typography-product-caption px-4 py-3 text-color-text-lightmode-secondary">
                No match. Please continue typing location.
              </div>
            )}

            {/* Loading state */}
            {isSearching && (
              <div className="px-4 py-2">
                <LoadingSkeleton height={24} className="mb-2" />
                <LoadingSkeleton height={24} className="mb-2" />
                <LoadingSkeleton height={24} />
              </div>
            )}

            {/* Results */}
            {!isSearching && hasResults && !isBelowMinLength && (
              <Combobox.Options
                static
                className={classNames(
                  'max-h-60 overflow-auto focus:outline-none',
                  !dropdownClassName &&
                    'typography-product-body sm:typography-product-caption py-2',
                )}
              >
                {suggestedAddresses.map((address) => (
                  <Combobox.Option
                    key={address.place_id}
                    value={address}
                    className={
                      optionClassName ||
                      'relative cursor-pointer select-none py-2 pl-4 pr-4 hover:bg-color-bg-lightmode-invert hover:text-color-text-lightmode-invert'
                    }
                  >
                    {({ selected }) => (
                      <span className={classNames('block truncate', selected && 'font-semibold')}>
                        {address.description}
                      </span>
                    )}
                  </Combobox.Option>
                ))}
              </Combobox.Options>
            )}

            {/* No results message */}
            {showNoResults && !isBelowMinLength && (
              <div className="typography-product-caption px-4 py-3 text-color-text-lightmode-secondary">
                {isGoogleMapsAvailable
                  ? 'No locations found. Try a different search.'
                  : 'Address search is temporarily unavailable. Please try refreshing the page.'}
              </div>
            )}
          </div>
        )}
      </div>
    </Combobox>
  );
}
